// Heuristic classifier that suggests a WorkItemType from the title + optional
// description. Pure function, no AI call — runs on every keystroke without
// network cost. The recommendation in the UI improvement programme (§5.5)
// asks for "auto-suggest type + placement on creation"; this is the type-
// suggestion half. Placement (target date / scheduling) is already handled
// by the existing slot-suggestion flow.
//
// The classifier returns a hinted type along with confidence (so the UI can
// stay quiet on weak matches and not nag the user). Order of patterns matters:
// the first match wins, so put the more specific phrases first.

export type WorkItemTypeName =
  | 'Task'
  | 'Decision'
  | 'Briefing'
  | 'Review'
  | 'FollowUp'
  | 'Draft'
  | 'Reading';

interface Suggestion {
  type: WorkItemTypeName;
  confidence: 'high' | 'medium';
  reason: string;
}

const RULES: Array<{
  pattern: RegExp;
  type: WorkItemTypeName;
  confidence: 'high' | 'medium';
  reason: string;
}> = [
  // Decisions — most explicit signal first
  { pattern: /\b(decide|decision|approve|approval|sign[- ]?off|choose between|go\/no[- ]?go)\b/i,
    type: 'Decision', confidence: 'high', reason: 'mentions a decision or approval' },
  // Briefing — meeting prep / pre-read
  { pattern: /\b(brief(ing)?|prep(arations?)?|pre[- ]?read|talking points|agenda)\b/i,
    type: 'Briefing', confidence: 'high', reason: 'looks like meeting prep' },
  // Reading
  { pattern: /\b(read|review (the )?(article|paper|book|report)|article|whitepaper|study)\b/i,
    type: 'Reading', confidence: 'high', reason: 'mentions reading material' },
  // Follow-up
  { pattern: /\b(follow[- ]?up|chase|check in with|circle back)\b/i,
    type: 'FollowUp', confidence: 'high', reason: 'mentions a follow-up' },
  // Review (operational, not reading)
  { pattern: /\b(review|audit)\b/i,
    type: 'Review', confidence: 'medium', reason: 'mentions a review' },
  // Draft
  { pattern: /\b(draft(ing)?|write (a|an|the))\b/i,
    type: 'Draft', confidence: 'medium', reason: 'mentions drafting' },
];

/**
 * Inspect title + description and return a hinted type when the text reads
 * unambiguously as one of the non-Task variants. Returns null when the
 * default Task is the right answer (no nag in that case).
 */
export function suggestWorkItemType(title: string, description?: string | null): Suggestion | null {
  const haystack = `${title ?? ''} ${description ?? ''}`.trim();
  if (haystack.length < 4) return null;

  for (const rule of RULES) {
    if (rule.pattern.test(haystack)) {
      return { type: rule.type, confidence: rule.confidence, reason: rule.reason };
    }
  }
  return null;
}
